import { Box, Section, Text } from '@factorialco/deprecated-design-system'
import { CompensationSettingFragment } from 'generated/resources/graphql'

import Select from 'modules/core/components/Select'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import useUpsertCompensationSetting from 'modules/compensations/hooks/useUpsertCompensationSetting'

// TODO: The backend cannot manage empty values, so we need to set a default value. This is covering until
// the backend manages this default setting from the onboarding task.
const DEFAULT_ALERT_DAY = 15

interface Props {
  compensationSettings: CompensationSettingFragment[] | undefined
}

const CompensationSettings = ({ compensationSettings }: Props) => {
  const { company } = useCurrentSession()
  const { upsert } = useUpsertCompensationSetting()

  const currentAlertDay = compensationSettings?.[0]?.pendingCompensationsAlertDay ?? undefined

  const alertDayOptions = Array.from({ length: 30 }, (_, i) => ({
    label: i18n.t(
      'compensations.settings.compensation_settings.pending_compensations.alert_day_selector',
      { day_number: i + 1 }
    ),
    value: i + 1,
  }))

  const onChange = async (value: string) => {
    const selected = value ? Number(value) : DEFAULT_ALERT_DAY
    const result = await upsert({
      companyId: company.id,
      pendingCompensationsAlertDay: selected,
    })

    const { success } = handleResult(result, () => undefined)

    if (success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    }
  }

  return (
    <Section
      illustration="settings"
      title={i18n.t('compensations.settings.compensation_settings.title')}
      description={i18n.t('compensations.settings.compensation_settings.description')}
    >
      <Box
        flexDirection="column"
        gap="s16"
        background="grey200"
        padding="s24"
        border={{
          all: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
        }}
        borderRadius={{
          all: 'abs008',
        }}
      >
        <Text.Normal weight="semibold" size="200">
          {i18n.t('compensations.settings.compensation_settings.pending_compensations.title')}
        </Text.Normal>
        <Text.Normal size="100">
          {i18n.t('compensations.settings.compensation_settings.pending_compensations.description')}
        </Text.Normal>
        <Select
          none={i18n.t('common.select')}
          value={(currentAlertDay ?? DEFAULT_ALERT_DAY).toString()}
          onChange={(value) => onChange(value)}
          options={alertDayOptions}
          missingLabel={i18n.t('common.missing_field')}
        />
      </Box>
    </Section>
  )
}

export default CompensationSettings
